import { MIN_PASSWORD_LENGTH } from './auth-fields'

/**
 * pt-BR messages for Better Auth's error codes.
 *
 * `specs/01`: server functions throw typed errors and the client maps them to pt-BR. The
 * mapping lives on the client side of the boundary because the messages are UI copy, and
 * because Better Auth's own endpoints answer with codes rather than prose.
 *
 * The default matters as much as the entries. An unmapped code must produce something a
 * seller can act on, not the English string that happened to arrive.
 */
const MESSAGES: Record<string, string> = {
  INVALID_ORIGIN: 'Este app não está autorizado a entrar neste servidor.',
  INVALID_EMAIL_OR_PASSWORD: 'E-mail ou senha incorretos.',
  INVALID_EMAIL: 'Informe um e-mail válido.',
  USER_ALREADY_EXISTS: 'Já existe uma conta com este e-mail.',
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: 'Já existe uma conta com este e-mail.',
  PASSWORD_TOO_SHORT: `A senha precisa ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`,
  PASSWORD_TOO_LONG: 'A senha é longa demais.',
  /** `/change-password` rejecting the current password the form supplied. */
  INVALID_PASSWORD: 'Senha atual incorreta.',
  /** `/change-password` on an account that only has a social provider. */
  CREDENTIAL_ACCOUNT_NOT_FOUND: 'Esta conta não usa senha para entrar.',
  EMAIL_NOT_VERIFIED: 'Confirme seu e-mail antes de entrar.',
  /** Email-OTP plugin: the verification code. */
  INVALID_OTP: 'Código incorreto. Confira o e-mail e tente de novo.',
  OTP_EXPIRED: 'Este código expirou. Peça um novo abaixo.',
  TOO_MANY_ATTEMPTS: 'Muitas tentativas com este código. Peça um novo abaixo.',
  /** A password-reset link that expired, was already used, or never existed. */
  INVALID_TOKEN:
    'Este link de redefinição expirou ou já foi usado. Peça um novo.',
  SESSION_EXPIRED: 'Sua sessão expirou. Entre novamente.',
  /** Raised by the `session.create.before` hook in `src/server/auth/index.ts`. */
  ACCOUNT_DEACTIVATED:
    'Esta conta foi desativada. Fale com o suporte para reativá-la.',
  /** A social sign-in onto an unverified same-email password account (`ACCOUNT_OPTIONS`). */
  ACCOUNT_NOT_LINKED:
    'Já existe uma conta com este e-mail. Entre com e-mail e senha e confirme seu e-mail em Minha conta.',
  /** OAuth callback codes, appended to `errorCallbackURL` as `?error=`. */
  ACCESS_DENIED: 'A autorização foi cancelada.',
  /** Apple's equivalent of `access_denied`. */
  USER_CANCELLED_AUTHORIZE: 'A autorização foi cancelada.',
  STATE_MISMATCH: 'A tentativa expirou. Tente novamente.',
  /** Linking: the Google account already signs in to another AiLucrei account. */
  ACCOUNT_ALREADY_LINKED_TO_DIFFERENT_USER:
    'Esta conta Google já está conectada a outra conta AiLucrei.',
  /** Linking: different-email accounts never merge (`ACCOUNT_OPTIONS`). */
  EMAIL_DOES_NOT_MATCH: 'Use uma conta Google com o mesmo e-mail desta conta.',
}

/** HTTP statuses that mean something specific regardless of the code that came with them. */
const STATUS_MESSAGES: Record<number, string> = {
  429: 'Muitas tentativas. Aguarde um minuto e tente de novo.',
}

export function authErrorMessage(error?: {
  code?: string
  status?: number
  message?: string
}): string {
  if (!error) return 'Não foi possível concluir. Tente novamente.'
  const code = error.code?.toUpperCase()
  if (code && MESSAGES[code]) return MESSAGES[code]
  if (error.status && STATUS_MESSAGES[error.status])
    return STATUS_MESSAGES[error.status]
  return 'Não foi possível concluir. Tente novamente.'
}
